import { useState } from "react";
import { motion } from "framer-motion";
import { Play, Star, Share2 } from "lucide-react";
import { ShareCardDialog } from "@/components/site/ShareCard";
import { fileUrl } from "@/lib/api";
import { divisionName } from "@/lib/content";

export const AthleteCard = ({ athlete: a, index = 0, onOpen }) => {
  const wins = (a.accomplishments || "").split("\n").filter(Boolean);
  const [sharing, setSharing] = useState(false);
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.08 }}
      className="group relative overflow-hidden bg-ink border border-line hover:border-gold transition-[border-color,transform] duration-300 hover:-translate-y-1"
      data-testid={`athlete-card-${a.id}`}
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-gradient-to-br from-navy to-pitch">
        {a.photo_url ? (
          <img src={fileUrl(a.photo_url)} alt={a.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="absolute inset-0 stripes" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-pitch via-pitch/30 to-transparent" />
        <span className="absolute -right-2 top-2 font-display text-[7rem] font-black italic leading-none text-stroke">{a.jersey || ""}</span>
        <div className="absolute left-4 top-4 flex gap-2">
          <span className="bg-gold px-2.5 py-1 font-display text-sm font-extrabold uppercase text-navy">{divisionName(a.division)}</span>
          {a.featured && <span className="flex items-center gap-1 bg-white px-2 py-1 font-mono text-[10px] uppercase text-navy"><Star className="h-3 w-3 fill-navy" />Featured</span>}
        </div>
        <div className="absolute inset-x-0 bottom-0 p-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">{a.position || "Athlete"}{a.jersey ? ` • #${a.jersey}` : ""}</p>
          <h3 className="mt-1 font-display text-3xl font-black uppercase italic leading-none">{a.name}</h3>
          {a.home_org && <p className="mt-1 text-xs uppercase tracking-wider text-slate-300">{a.home_org}</p>}
        </div>
      </div>
      {(
        <div className="border-t border-line p-5 space-y-3">
          {a.spotlight_week && <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Weekly Spotlight • {a.spotlight_week}</p>}
          {wins.length > 0 && (
            <ul className="space-y-1.5 text-sm text-slate-300">
              {wins.slice(0, 3).map((w) => <li key={w} className="flex gap-2"><span className="text-gold">▸</span>{w}</li>)}
            </ul>
          )}
          {a.media_url && onOpen && (
            <button onClick={() => onOpen(a)} className="inline-flex items-center gap-2 font-display font-bold uppercase text-gold hover:text-white transition-colors" data-testid={`athlete-media-button-${a.id}`}>
              <Play className="h-4 w-4 fill-current" /> Featured Media
            </button>
          )}
          <button onClick={() => setSharing(true)} className="flex w-full items-center justify-center gap-2 border border-line py-2.5 font-display font-bold uppercase tracking-wider text-slate-200 hover:border-gold hover:text-gold transition-colors" data-testid={`athlete-share-button-${a.id}`}>
            <Share2 className="h-4 w-4" /> Share Card
          </button>
        </div>
      )}
      {sharing && <ShareCardDialog athlete={a} onClose={() => setSharing(false)} />}
    </motion.article>
  );
};
